/** Warstwa bezpieczeństwa SmartLight (decyzje JKO 2026-08-03, ADR w repo):
 *
 * 1. PIN to LOKALNA BLOKADA, nie tożsamość — odblokowuje klucz danych.
 *    Klucz danych (AES-GCM) jest owinięty kluczem z PIN (PBKDF2, sól per
 *    urządzenie). N błędnych PIN → wymazanie opakowanego klucza: dane stają się
 *    nieodczytywalne, wymagane ponowne logowanie ONLINE.
 * 2. Transakcje: append-only z ŁAŃCUCHEM HASHY (każde ogniwo niesie hash
 *    poprzedniego) — cicha zmiana po fakcie jest wykrywalna lokalnie i przy
 *    synchronizacji (SaleLight sprawdza ciągłość od ostatniego znanego ogniwa).
 * 3. Klucz podpisu 2D: ECDSA P-256 non-extractable — prywatny nie opuszcza
 *    urządzenia nawet przy pełnym zrzucie IndexedDB.
 */

import { keys, kv } from "./db";

const PBKDF2_ITERS = 310_000;
export const MAX_PIN_ATTEMPTS = 5;

const enc = new TextEncoder();
const dec = new TextDecoder();

function b64(buf: ArrayBuffer): string {
  return btoa(String.fromCharCode(...new Uint8Array(buf)));
}
function b64d(s: string): Uint8Array {
  return Uint8Array.from(atob(s), (c) => c.charCodeAt(0));
}
export function b64url(buf: ArrayBuffer): string {
  return b64(buf).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** Kanoniczny JSON — MUSI dawać bajt w bajt to samo, co po stronie Pythona
 * `json.dumps(..., separators=(",",":"), sort_keys=True, ensure_ascii=False)`.
 * Dlatego: klucze sortowane rekurencyjnie, bez spacji, bez escapowania non-ASCII
 * (JSON.stringify i tak nie escapuje), a w transakcjach NIE używamy floatów. */
export function canonical(value: unknown): string {
  if (value === null || typeof value !== "object") return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(canonical).join(",")}]`;
  const obj = value as Record<string, unknown>;
  const parts = Object.keys(obj)
    .filter((k) => obj[k] !== undefined)
    .sort()
    .map((k) => `${JSON.stringify(k)}:${canonical(obj[k])}`);
  return `{${parts.join(",")}}`;
}

export async function sha256hex(data: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", enc.encode(data));
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

// ── klucz danych chroniony PIN-em ────────────────────────────────────────────

async function pinKey(pin: string, salt: Uint8Array): Promise<CryptoKey> {
  const base = await crypto.subtle.importKey("raw", enc.encode(pin), "PBKDF2", false,
    ["deriveKey"]);
  return crypto.subtle.deriveKey(
    { name: "PBKDF2", salt: salt as BufferSource, iterations: PBKDF2_ITERS, hash: "SHA-256" },
    base, { name: "AES-GCM", length: 256 }, false, ["wrapKey", "unwrapKey"],
  );
}

let dataKey: CryptoKey | null = null; // odblokowany klucz danych (tylko w pamięci)

export function isUnlocked(): boolean { return dataKey !== null; }
export function lock(): void { dataKey = null; }

export async function setPin(pin: string): Promise<void> {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const key = await crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, true,
    ["encrypt", "decrypt"]);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const wrapped = await crypto.subtle.wrapKey("raw", key, await pinKey(pin, salt),
    { name: "AES-GCM", iv: iv as BufferSource });
  await kv.set("vault", { salt: b64(salt.buffer as ArrayBuffer),
    iv: b64(iv.buffer as ArrayBuffer), wrapped: b64(wrapped), attempts: 0 });
  dataKey = key;
}

export async function unlock(pin: string): Promise<"ok" | "bad-pin" | "wiped"> {
  const vault = await kv.get<{ salt: string; iv: string; wrapped: string;
    attempts: number }>("vault");
  if (!vault) return "wiped";
  try {
    dataKey = await crypto.subtle.unwrapKey(
      "raw", b64d(vault.wrapped) as BufferSource, await pinKey(pin, b64d(vault.salt)),
      { name: "AES-GCM", iv: b64d(vault.iv) as BufferSource },
      { name: "AES-GCM", length: 256 }, true, ["encrypt", "decrypt"],
    );
    await kv.set("vault", { ...vault, attempts: 0 });
    return "ok";
  } catch {
    const attempts = (vault.attempts ?? 0) + 1;
    if (attempts >= MAX_PIN_ATTEMPTS) {
      // wymazanie opakowanego klucza = dane nieodczytywalne; transakcje NIE giną
      // dla rozliczeń (zsynchronizowane są w SaleLight, niezsynchronizowanych
      // broni łańcuch — dziura będzie widoczna przy najbliższym sync)
      await kv.del("vault");
      return "wiped";
    }
    await kv.set("vault", { ...vault, attempts });
    return "bad-pin";
  }
}

export async function pinAttemptsLeft(): Promise<number> {
  const vault = await kv.get<{ attempts?: number }>("vault");
  return vault ? MAX_PIN_ATTEMPTS - (vault.attempts ?? 0) : 0;
}

export async function encryptJson(value: unknown): Promise<string> {
  if (!dataKey) throw new Error("magazyn zablokowany — podaj PIN");
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt({ name: "AES-GCM", iv: iv as BufferSource },
    dataKey, enc.encode(JSON.stringify(value)));
  return `${b64(iv.buffer as ArrayBuffer)}.${b64(ct)}`;
}

export async function decryptJson<T>(envelope: string): Promise<T> {
  if (!dataKey) throw new Error("magazyn zablokowany — podaj PIN");
  const [iv, ct] = envelope.split(".");
  const pt = await crypto.subtle.decrypt({ name: "AES-GCM", iv: b64d(iv) as BufferSource },
    dataKey, b64d(ct) as BufferSource);
  return JSON.parse(dec.decode(pt)) as T;
}

// ── klucz podpisu urządzenia (ES256) ─────────────────────────────────────────

export async function deviceKeys(): Promise<CryptoKeyPair> {
  const existing = await keys.get("device");
  if (existing) return existing;
  const pair = await crypto.subtle.generateKey(
    { name: "ECDSA", namedCurve: "P-256" },
    false, // non-extractable — klucz prywatny NIE opuszcza urządzenia
    ["sign", "verify"],
  );
  await keys.set("device", pair);
  return pair;
}

export async function devicePublicJwk(): Promise<JsonWebKey> {
  const pair = await deviceKeys();
  const jwk = await crypto.subtle.exportKey("jwk", pair.publicKey);
  return { kty: jwk.kty, crv: jwk.crv, x: jwk.x, y: jwk.y };
}

/** Odcisk NASZEGO klucza publicznego (RFC 7638) — po nim rozpoznajemy, czy klucz
 * zarejestrowany na serwerze pod tym id urządzenia jest nasz, czy z innej instalacji. */
export async function deviceKeyThumbprint(): Promise<string> {
  const jwk = await devicePublicJwk() as Record<string, string>;
  const kanoniczny = JSON.stringify({ crv: jwk.crv, kty: jwk.kty, x: jwk.x, y: jwk.y });
  const skrot = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(kanoniczny));
  return btoa(String.fromCharCode(...new Uint8Array(skrot)))
    .replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** Token 2D biletu offline — TA SAMA koperta co HMAC SaleLighta (gzip+base64url),
 * tylko alg=ES256 i kid=id urządzenia. Podpis WebCrypto to surowe r||s. */
export async function signTicketToken(
  payload: Record<string, unknown>, deviceId: string,
): Promise<string> {
  const pair = await deviceKeys();
  const body = canonical(payload);
  const sig = await crypto.subtle.sign({ name: "ECDSA", hash: "SHA-256" },
    pair.privateKey, enc.encode(body));
  const blob = JSON.stringify({ d: JSON.parse(body), sig: b64url(sig),
    kid: deviceId, alg: "ES256" });
  // gzip przez CompressionStream (ta sama treść co po stronie Pythona)
  const gz = await new Response(
    new Blob([blob]).stream().pipeThrough(new CompressionStream("gzip")),
  ).arrayBuffer();
  return `Z1.${b64url(gz)}`;
}
